"use client";

import { Loader2, ShoppingCart } from "lucide-react";
import { useEffect, useRef, useState, useTransition } from "react";
import { useRouter } from "next/navigation";

import { useCart } from "../hooks/useCart";
import { useAppSelector } from "@/src/store/hooks";
import { cn } from "@/src/lib/utils/cn";
import { ROUTES } from "@/src/constants/routes";

type AddToCartButtonProps = {
  variantId: number | null;
  variantIds?: number[];
  isAvailable: boolean;
  className?: string;
  iconClassName?: string;
  iconOnly?: boolean;
  ariaLabel?: string;
  label?: string;
  activeLabel?: string;
  mode?: "toggle" | "add";
  redirectToCartOnSuccess?: boolean;
  detailsVariantId?: number | null;
};

const CART_DETAILS_VARIANT_MAP_KEY = "cart-details-variant-map";

const addToCartButtonClass =
  "inline-flex flex-1 items-center justify-center gap-[0.65rem] rounded-full border border-[rgba(215,194,165,0.42)] bg-[var(--color-surface-strong)]  py-2 text-[0.95rem] font-semibold text-[var(--color-ink)] transition hover:-translate-y-[2px] hover:bg-[var(--color-paper)]";


function rememberCartDetailsVariant(
  selectedVariantIds: number[],
  detailsVariantId?: number | null,
) {
  if (
    typeof window === "undefined" ||
    !detailsVariantId ||
    !Number.isInteger(detailsVariantId) ||
    detailsVariantId <= 0
  ) {
    return;
  }

  try {
    const rawMap = window.localStorage.getItem(CART_DETAILS_VARIANT_MAP_KEY);
    const currentMap = rawMap ? (JSON.parse(rawMap) as Record<string, number>) : {};

    selectedVariantIds.forEach((selectedVariantId) => {
      if (Number.isInteger(selectedVariantId) && selectedVariantId > 0) {
        currentMap[String(selectedVariantId)] = detailsVariantId;
      }
    });

    window.localStorage.setItem(
      CART_DETAILS_VARIANT_MAP_KEY,
      JSON.stringify(currentMap),
    );
  } catch {
    // Ignore storage failures; the cart can still use the selected variant id.
  }
}
export default function AddToCartButton({
  variantId,
  variantIds,
  isAvailable,
  className,
  iconClassName = "h-4 w-4",
  iconOnly = false,
  ariaLabel,
  label = "السلة",
  activeLabel,
  mode = "toggle",
  redirectToCartOnSuccess = false,
  detailsVariantId = null,
}: AddToCartButtonProps) {
  const router = useRouter();
  const [isPressed, setIsPressed] = useState(false);
  const [isJustAdded, setIsJustAdded] = useState(false);
  const justAddedTimerRef = useRef<number | null>(null);
  const redirectTimerRef = useRef<number | null>(null);
  const [isPending, startTransition] = useTransition();
  const { cart, toggleItemByVariant, addItems, isMutating, mutationState } = useCart({
    autoLoad: false,
  });
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
  const selectedVariantIds =
    variantIds && variantIds.length > 0
      ? Array.from(new Set(variantIds))
      : variantId
        ? [variantId]
        : [];
  const primaryVariantId = variantId ?? selectedVariantIds[0] ?? null;
  const isInCartFromStore = useAppSelector((state) =>
    selectedVariantIds.length > 0
      ? selectedVariantIds.every((selectedId) =>
          state.cart.variantIds.includes(selectedId),
        )
      : false,
  );
  const isAddingThisVariant =
    mutationState?.type === "add" &&
    typeof mutationState.variantId === "number" &&
    selectedVariantIds.includes(mutationState.variantId);
  const isRemovingThisVariant =
    mutationState?.type === "remove" &&
    cart?.items.some(
      (item) =>
        item.itemId === mutationState.itemId &&
        selectedVariantIds.includes(item.variantId),
    );
  const isInCart = Boolean(
    isInCartFromStore ||
    (selectedVariantIds.length > 0 &&
      cart &&
      selectedVariantIds.every((selectedId) =>
        cart.items.some((item) => item.variantId === selectedId),
      )),
  );
  const isActiveVisual =
    isPressed ||
    isJustAdded ||
    isPending ||
    isAddingThisVariant ||
    Boolean(isRemovingThisVariant) ||
    isInCart ||
    (iconOnly && isInCart);

  useEffect(() => {
    return () => {
      if (justAddedTimerRef.current !== null) {
        window.clearTimeout(justAddedTimerRef.current);
      }
    };
  }, []);

  const isDisabled =
    !isAvailable || selectedVariantIds.length === 0 || isPending || isMutating;
  const defaultAriaLabel = isAvailable
    ? isInCart
      ? "حذف المنتج من السلة"
      : "أضف المنتج إلى السلة"
    : "المنتج غير متاح الآن";
  const buttonAriaLabel = ariaLabel ?? (iconOnly ? defaultAriaLabel : undefined);

  return (
    <button
      type="button"
      className={cn(
        addToCartButtonClass,
        "disabled:cursor-not-allowed disabled:opacity-55 disabled:hover:translate-y-0",
        className,
        isActiveVisual
          ? "border-transparent bg-[#b98922] text-white"
          : "",
      )}
      disabled={isDisabled}
      aria-disabled={isDisabled}
      aria-label={buttonAriaLabel}
      onPointerDown={() => setIsPressed(true)}
      onPointerUp={() => setIsPressed(false)}
      onPointerLeave={() => setIsPressed(false)}
      onBlur={() => setIsPressed(false)}
      onClick={() => {
        if (selectedVariantIds.length === 0 || !isAvailable) {
          return;
        }

        rememberCartDetailsVariant(selectedVariantIds, detailsVariantId);

        setIsJustAdded(true);
        if (justAddedTimerRef.current !== null) {
          window.clearTimeout(justAddedTimerRef.current);
        }
        justAddedTimerRef.current = window.setTimeout(() => {
          setIsJustAdded(false);
        }, 700);

        startTransition(() => {
          void (async () => {
            const didAdd = mode === "add" || selectedVariantIds.length > 1
              ? await addItems(selectedVariantIds)
              : primaryVariantId
                ? await toggleItemByVariant(primaryVariantId)
                : false;

            if (!didAdd || (isAuthenticated && !redirectToCartOnSuccess)) {
              return;
            }

            if (redirectTimerRef.current !== null) {
              window.clearTimeout(redirectTimerRef.current);
            }

            rememberCartDetailsVariant(selectedVariantIds, detailsVariantId);

            const params = new URLSearchParams({
              highlight: selectedVariantIds.join(","),
            });

            if (
              detailsVariantId &&
              Number.isInteger(detailsVariantId) &&
              detailsVariantId > 0
            ) {
              params.set(
                "details",
                selectedVariantIds
                  .map((selectedVariantId) => `${selectedVariantId}:${detailsVariantId}`)
                  .join(","),
              );
            }

            redirectTimerRef.current = window.setTimeout(() => {
              router.push(`${ROUTES.CART}?${params.toString()}`);
            }, 1000);
          })();
        });
        setIsPressed(false);
      }}
    >
      {isAvailable ? (
        isPending || isAddingThisVariant ? (
          <span className="inline-flex items-center gap-2">
            <Loader2 className={cn(iconClassName, "animate-spin")} />
            {iconOnly ? null : label}
          </span>
        ) : iconOnly ? (
          <ShoppingCart className={iconClassName} />
        ) : isInCart ? (
          <span className="inline-flex items-center gap-2">
            <ShoppingCart className={iconClassName} />
            <span>{activeLabel ?? label}</span>
          </span>
        ) : (
          <span className="inline-flex items-center gap-2">
            <ShoppingCart className={iconClassName} />
            <span>{label}</span>
          </span>
        )
      ) : iconOnly ? (
        <ShoppingCart className={iconClassName} />
      ) : (
        "غير متاح الآن"
      )}
    </button>
  );
}
